import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType
} from 'react-router-dom';

// SENTRY_DSN does not change across envs and is not a secret, so we can commit it to source code.
// https://docs.sentry.io/concepts/key-terms/dsn-explainer/
const SENTRY_DSN =
  'https://0a32bb32d6f71ca5ad1afdcd295d77ea@o152170.ingest.us.sentry.io/4507961579732992';

// Figure out what env Sentry should log on error
function getSentryEnvironment(hostname: string): string {
  if (hostname === 'publishing-engine-demo.morressier.com') {
    return 'scup-staging';
  }

  if (hostname === 'scup-preprod.morressier.com') {
    return 'scup-preproduction';
  }

  if (hostname === 'scup.publishing-engine.com') {
    return 'scup-production';
  }

  if (hostname === 'sales-publishing-engine.morressier.com') {
    return 'sales-staging';
  }

  if (hostname === 'mag-staging-publishing-engine.morressier.com') {
    return 'mag-staging';
  }

  if (hostname === 'mag.publishing-engine.com') {
    return 'mag-production';
  }

  if (hostname === 'berghahn-staging-publishing-engine.morressier.com') {
    return 'berghahn-staging';
  }

  if (hostname === 'berghahn.publishing-engine.com') {
    return 'berghahn-production';
  }

  // This should never be logged as we filter out localhost before we send
  // but keeping if you want to manually enable localhost for some debugging
  if (hostname === 'localhost') {
    return 'development';
  }

  // This should never be reached, but leaving it like this so we know something
  // goes wrong if Sentry logs "unknown"
  return 'unknown';
}

Sentry.init({
  dsn: SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    })
  ],
  beforeSend(event) {
    // Don't send errors if we're on localhost
    if (window.location.hostname === 'localhost') {
      return null;
    }
    return event;
  },
  // Tracing
  tracesSampleRate: 0.2,
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    'localhost',
    /^https:\/\/publishing-staging\.morressier\.com\/api/,
    /^https:\/\/publishing\.morressier\.com\/api/,
    /^https:\/\/publishing-demo[a-z0-9]*\.morressier\.com\/api/,
    /^https:\/\/bnj\.publishing-engine\.com\/api/,
    /^https:\/\/demo-staging\.molecularconnections\.com\/api/,
    /^https:\/\/bnj-preprod\.publishing-engine\.com\/api/
  ],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  environment: (() => {
    const hostname = window.location.hostname;
    const env = getSentryEnvironment(hostname);

    return env;
  })()
});

//   // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
//   tracePropagationTargets: [/^\//, /^https:\/\/yourserver\.io\/api/],

//   // Capture Replay for 10% of all sessions,
//   // plus for 100% of sessions with an error
//   replaysSessionSampleRate: 0.1,
//   replaysOnErrorSampleRate: 1.0,
// });
